// Client-error classification + diagnostics (NEA-233), shared by the error boundaries and the
// server-side logger. It lives here rather than in logError.ts because that file is 'use server'
// and may only export async functions — which is why the "Load failed" literals were duplicated
// between the logger's noise filter and nothing else. One definition now.

// A cancelled in-flight fetch or RSC navigation, NOT a failure of ours: Safari says
// "Load failed", Chrome says "Failed to fetch". Safari aborts in-flight requests when a tab is
// backgrounded or the phone locks, so a player who switches apps mid-navigation lands here.
//
// ⚠️ Matched EXACTLY, and it must stay that way. App code produces messages like
// "Failed to fetch user: 500" that genuinely are our bug — a substring match would silently
// reclassify real defects as network noise, which is the one mistake this function can make.
export function isTransientFetchError(error: { message?: string } | string | null | undefined): boolean {
  const raw = typeof error === 'string' ? error : error?.message
  const m = raw?.trim().toLowerCase()
  return m === 'load failed' || m === 'failed to fetch'
}

// A coarse "who was this" label for the admin error list — browser family + platform, e.g.
// "Safari · iOS". Deliberately not a full UA parser: the raw string is stored alongside it, so
// when this is wrong the evidence is still there. Order matters — Chrome and Edge both carry
// "Safari" in their UA, and the iOS browsers all carry "Safari" because they must.
export function describeUserAgent(ua: string | null | undefined): string | null {
  if (!ua) return null

  const browser =
      /Edg[A-Z]?\//.test(ua)              ? 'Edge'
    : /(?:CriOS|Chrome)\//.test(ua)       ? 'Chrome'
    : /(?:FxiOS|Firefox)\//.test(ua)      ? 'Firefox'
    : /Safari\//.test(ua)                 ? 'Safari'
    : null

  const platform =
      /iPhone|iPad|iPod/.test(ua)         ? 'iOS'
    : /Android/.test(ua)                  ? 'Android'
    : /Macintosh|Mac OS X/.test(ua)       ? 'macOS'
    : /Windows/.test(ua)                  ? 'Windows'
    : /Linux/.test(ua)                    ? 'Linux'
    : null

  if (!browser && !platform) return null
  return [browser, platform].filter(Boolean).join(' · ')
}

// Client-only context the SERVER cannot see. The user agent is deliberately NOT collected here
// — logClientError reads it from the request headers, so a new call site cannot forget it
// (NEA-227's lesson). Only the connection type has to come from the browser, and if a caller
// omits it the record still carries the user agent rather than being lost.
export function clientDiagnostics(): { connection?: string } {
  if (typeof navigator === 'undefined') return {}
  // Network Information API — Chromium and Android only; absent on Safari and Firefox, which is
  // itself informative, so an undefined value is left out rather than written as "unknown".
  const conn = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
  return conn?.effectiveType ? { connection: conn.effectiveType } : {}
}
